import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import {
  SalaryHistoryDbType,
  SalaryHistoryTravelItem,
  SalaryItemRate,
} from '../../../../api/types/accountants/salary-history/salary-history.type';
import appApi from '../../../../api/app-api';
import { LegalEntityType } from '../../../user-page/roles/lecturer-price-rate/componenets/acountant/user-page-lecturer-rate-accountant';
import {
  getItemAdjustedLineAmount,
  getItemBilledQuantity,
} from '../utils/salary-break-calculations';

interface SalaryExportDialogProps {
  visible: boolean;
  onHide: () => void;
  /** כל המשכורות המוצגות בעמוד הנוכחי (לא רק סימון שורות — כדי שלא יחסרו מרצים בייצוא) */
  exportRows: SalaryHistoryDbType[];
  startDate: Date | null;
  endDate: Date | null;
  branchId: number | null;
}

const sortCodes = (codes: Iterable<string>): string[] => {
  return Array.from(new Set(codes)).sort((a, b) =>
    a.localeCompare(b, 'he', { numeric: true, sensitivity: 'base' }),
  );
};

const itemLineQuantity = (item: SalaryItemRate): number =>
  getItemBilledQuantity(item);

const strFrom = (o: Record<string, unknown>, ...keys: string[]): string => {
  for (const k of keys) {
    const v = o[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
};

/** מזהה עמודה במכפ״ל עבור שורות שכר: קודם קוד הנה״ח (accountingReportCode), ואם חסר — קוד דיווח. */
const itemMakifColumnCode = (item: SalaryItemRate): string => {
  const o = item as unknown as Record<string, unknown>;
  const acc = strFrom(o, 'accountingReportCode', 'accounting_report_code');
  if (acc) return acc;
  return strFrom(o, 'reportingCode', 'reporting_code');
};

/** קוד עמודת נסיעות במכפ״ל — רק `accountingTravelReportCode` (ללא נפילה ל-reportingCode) */
const accountingTravelCodeOnly = (t: SalaryHistoryTravelItem): string => {
  const o = t as unknown as Record<string, unknown>;
  return strFrom(
    o,
    'accountingTravelReportCode',
    'accounting_travel_report_code',
  );
};

const collectCodesForEntityTypes = (
  salaries: SalaryHistoryDbType[],
  targetEntityTypes: LegalEntityType[],
): string[] => {
  const codes = new Set<string>();
  salaries.forEach((salary) => {
    salary.items.forEach((item) => {
      if (!targetEntityTypes.includes(item.legalEntityType)) return;
      const c = itemMakifColumnCode(item);
      if (c) codes.add(c);
    });
    (salary.travelItems ?? []).forEach((t) => {
      if (
        t.legalEntityType != null &&
        !targetEntityTypes.includes(t.legalEntityType)
      ) {
        return;
      }
      const c = accountingTravelCodeOnly(t);
      if (c) codes.add(c);
    });
  });
  return sortCodes(codes);
};

const buildMakifRows = (
  salaries: SalaryHistoryDbType[],
  targetEntityTypes: LegalEntityType[],
  orderedCodes: string[],
): (string | number)[][] => {
  const codeIndex = new Map<string, number>();
  orderedCodes.forEach((c, i) => codeIndex.set(c, i));

  const baseLen = 2 + orderedCodes.length * 2 + 2;

  const emptyPairRow = (): (string | number)[] =>
    Array.from({ length: baseLen }, () => '');

  /** אגרגציה לפי userId + קוד (כל משכורות אותו מרצה בעמוד) — כמו קובץ רצוי */
  const byUserId = new Map<number, SalaryHistoryDbType[]>();
  salaries.forEach((s) => {
    const list = byUserId.get(s.userId) ?? [];
    list.push(s);
    byUserId.set(s.userId, list);
  });

  const userOrder: number[] = [];
  const seenUser = new Set<number>();
  salaries.forEach((s) => {
    if (!seenUser.has(s.userId)) {
      seenUser.add(s.userId);
      userOrder.push(s.userId);
    }
  });

  type ItemAgg = {
    sumQty: number;
    sumLineAmount: number;
    sumAdjustedLineAmount: number;
  };
  type TravelAgg = { unitCount: number; sumAmount: number };

  const rows: (string | number)[][] = [];

  userOrder.forEach((userId) => {
    const userSalaries = byUserId.get(userId) ?? [];
    if (userSalaries.length === 0) return;

    const first = userSalaries[0];
    const nationalId = String(first.nationalId ?? '');
    const fullName = `${String(first.lastName ?? '').trim()} ${String(
      first.firstName ?? '',
    ).trim()}`.trim();

    // Items: split rows by (code + rate) per professor
    const itemByCodeRate = new Map<string, ItemAgg>();
    // Travel: split rows by (code + rate if exists) per professor (aggregate across dates)
    const travelByCodeRate = new Map<string, TravelAgg>();

    userSalaries.forEach((salary) => {
      salary.items.forEach((item) => {
        if (!targetEntityTypes.includes(item.legalEntityType)) return;
        const code = itemMakifColumnCode(item);
        if (!code || codeIndex.get(code) === undefined) return;

        const qty = itemLineQuantity(item);
        const price = Number(item.rate);
        if (!Number.isFinite(price)) return;

        const key = `${code}__RATE__${price}`;
        const prev = itemByCodeRate.get(key) ?? {
          sumQty: 0,
          sumLineAmount: 0,
          sumAdjustedLineAmount: 0,
        };
        prev.sumQty += qty;
        prev.sumLineAmount += qty * price;
        prev.sumAdjustedLineAmount += getItemAdjustedLineAmount(item);
        itemByCodeRate.set(key, prev);
      });

      (salary.travelItems ?? []).forEach((t) => {
        if (
          t.legalEntityType != null &&
          !targetEntityTypes.includes(t.legalEntityType)
        ) {
          return;
        }
        const code = accountingTravelCodeOnly(t);
        if (!code || codeIndex.get(code) === undefined) return;

        const amount = Number(t.amount) || 0;
        const rate = (t as unknown as { rate?: unknown }).rate;
        const rateKey =
          rate !== null && rate !== undefined && Number.isFinite(Number(rate))
            ? String(Number(rate))
            : '';
        const key = `${code}__RATE__${rateKey}`;

        const prev = travelByCodeRate.get(key) ?? {
          unitCount: 0,
          sumAmount: 0,
        };
        prev.unitCount += 1;
        prev.sumAmount += amount;
        travelByCodeRate.set(key, prev);
      });
    });

    // Emit item rows grouped by code, then by rate
    orderedCodes.forEach((code) => {
      const entries = Array.from(itemByCodeRate.entries()).filter(([k]) =>
        k.startsWith(`${code}__RATE__`),
      );
      // stable numeric rate ordering
      entries.sort((a, b) => {
        const ra = Number(a[0].split('__RATE__')[1]);
        const rb = Number(b[0].split('__RATE__')[1]);
        return ra - rb;
      });

      entries.forEach(([, agg]) => {
        if (agg.sumQty === 0 && agg.sumLineAmount === 0) return;
        const idx = codeIndex.get(code);
        if (idx === undefined) return;

        const qty = agg.sumQty;
        const lineTotal = agg.sumLineAmount;
        const adjustedLineTotal = agg.sumAdjustedLineAmount;
        const unitPrice = qty > 0 ? lineTotal / qty : 0;

        const row = emptyPairRow();
        row[0] = nationalId;
        row[1] = fullName;
        const colBase = 2 + idx * 2;
        row[colBase] = qty;
        row[colBase + 1] = unitPrice;
        row[baseLen - 2] = lineTotal;
        row[baseLen - 1] = adjustedLineTotal;
        rows.push(row);
      });
    });

    // Emit travel rows grouped by code, then by rateKey
    orderedCodes.forEach((code) => {
      const entries = Array.from(travelByCodeRate.entries()).filter(([k]) =>
        k.startsWith(`${code}__RATE__`),
      );
      entries.sort((a, b) => a[0].localeCompare(b[0], 'he', { numeric: true }));

      entries.forEach(([key, agg]) => {
        if (agg.unitCount === 0 && agg.sumAmount === 0) return;
        const idx = codeIndex.get(code);
        if (idx === undefined) return;

        const qty = agg.unitCount;
        const lineTotal = agg.sumAmount;
        const unitPrice = qty > 0 ? lineTotal / qty : 0;

        const row = emptyPairRow();
        row[0] = nationalId;
        row[1] = fullName;
        const colBase = 2 + idx * 2;
        row[colBase] = qty;
        row[colBase + 1] = unitPrice;
        row[baseLen - 2] = lineTotal;
        row[baseLen - 1] = lineTotal;
        rows.push(row);
      });
    });
  });

  return rows;
};

export const SalaryExportDialog: React.FC<SalaryExportDialogProps> = ({
  visible,
  onHide,
  exportRows,
  startDate,
  endDate,
  branchId,
}) => {
  const [exporting, setExporting] = useState(false);

  const fetchAllRows = async (): Promise<SalaryHistoryDbType[]> => {
    if (!startDate || !endDate || branchId == null) return exportRows;

    const limit = 1000;
    let offset = 0;
    let total = 0;
    const all: SalaryHistoryDbType[] = [];

    do {
      const res = await appApi.getSalaryHistory(
        startDate,
        endDate,
        branchId,
        offset,
        limit,
      );
      const page = res.data.body;
      total = page.total ?? 0;
      all.push(...(page.data ?? []));
      offset += limit;
    } while (all.length < total);

    return all;
  };

  const generateExcel = async (
    entityTypes: LegalEntityType[],
    fileName: string,
  ) => {
    setExporting(true);
    try {
      const rowsToExport = await fetchAllRows();
      const orderedCodes = collectCodesForEntityTypes(
        rowsToExport,
        entityTypes,
      );
      const dataRows = buildMakifRows(rowsToExport, entityTypes, orderedCodes);

      if (dataRows.length === 0) {
        alert('אין נתונים להצגה עבור הקטגוריה שנבחרה');
        return;
      }

      const pairHeaders = orderedCodes.flatMap((code) => [
        `כמות (${code})`,
        `מחיר (${code})`,
      ]);
      const headerRow = [
        'מספר זהות',
        'שם העובד',
        ...pairHeaders,
        'סה״כ',
        'סה״כ עם הפסקות',
      ];

      const excelData: (string | number)[][] = [headerRow, ...dataRows];

      const generateCustomExcel = (
        data: (string | number)[][],
        customFileName: string,
      ): void => {
        import('xlsx').then((XLSX) => {
          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.aoa_to_sheet(data);

          // SheetJS extended props (direction / workbook view) — not in minimal typings
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (worksheet as any)['!dir'] = 'rtl';
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (workbook as any).Workbook = { Views: [{ RTL: true }] };

          XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

          const binaryString = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'binary',
          });

          const buffer = new ArrayBuffer(binaryString.length);
          const view = new Uint8Array(buffer);
          for (let i = 0; i < binaryString.length; i++) {
            view[i] = binaryString.charCodeAt(i) & 0xff;
          }

          const blob = new Blob([buffer], { type: 'application/octet-stream' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = customFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
      };

      generateCustomExcel(excelData, fileName);
      onHide();
    } finally {
      setExporting(false);
    }
  };

  const handleEmployeeExport = () => {
    void generateExcel([LegalEntityType.EMPLOYEE], 'שכירים.xlsx');
  };

  const handleFreelancerExport = () => {
    void generateExcel(
      [
        LegalEntityType.VAT_REGISTERED_FREELANCER,
        LegalEntityType.VAT_EXEMPT_FREELANCER,
      ],
      'עצמאים.xlsx',
    );
  };

  const handleCompanyExport = () => {
    void generateExcel(
      [LegalEntityType.COMPANY, LegalEntityType.NONPROFIT],
      'חברות.xlsx',
    );
  };

  return (
    <Dialog
      header="יצוא נתוני שכר לאקסל"
      visible={visible}
      onHide={onHide}
      style={{ width: '450px', direction: 'rtl' }}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '0.5rem',
          }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-text"
          />
        </div>
      }
    >
      <div style={{ textAlign: 'right' }}>
        <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
          בחר את סוג הישות לייצוא נתוני השכר:
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Button
            label="שכירים"
            icon="pi pi-users"
            onClick={handleEmployeeExport}
            disabled={exporting}
            style={{
              backgroundColor: '#3B82F6',
              border: 'none',
              padding: '1rem',
              fontSize: '1rem',
              borderRadius: '0.5rem',
            }}
          />

          <Button
            label="עצמאים"
            icon="pi pi-user"
            onClick={handleFreelancerExport}
            disabled={exporting}
            style={{
              backgroundColor: '#10B981',
              border: 'none',
              padding: '1rem',
              fontSize: '1rem',
              borderRadius: '0.5rem',
            }}
          />

          <Button
            label="חברות"
            icon="pi pi-building"
            onClick={handleCompanyExport}
            disabled={exporting}
            style={{
              backgroundColor: '#8B5CF6',
              border: 'none',
              padding: '1rem',
              fontSize: '1rem',
              borderRadius: '0.5rem',
            }}
          />
        </div>

        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6B7280' }}>
            <strong>ייצוא:</strong> {exportRows.length} משכורות מהעמוד הנוכחי
          </p>
        </div>
      </div>
    </Dialog>
  );
};
